// ======================================================================
// 📁 ValidationOperationCard.js
// PURPOSE:
//   Renders validation result for a single operation.
// ======================================================================

import React, { useState } from "react";

export default function ValidationOperationCard({
  operation,
  isAccepted = false,
  onAcceptOperation,
}) {
  const [open, setOpen] = useState(true);

  const statusColor = {
    VALID: "success",
    ADJUSTED: "warning",
    BLOCKED: "danger",
  };

  return (
    <div className="card mb-3">
      {/* ============================================================= */}
      {/* HEADER */}
      {/* ============================================================= */}
      <div
        className="card-header d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer" }}
        onClick={() => setOpen(!open)}
      >
        <div>
          <strong>
            {operation.operation_seq_no}.{" "}
            {operation.operation_name ||
              `Operation ${operation.operation_seq_no}`}
          </strong>
        </div>

        <div className="d-flex align-items-center gap-2">
          {/* ---------------------------------------------------------
              🔧 [2026-01-17 22:25]
              Accepted indicator
              --------------------------------------------------------- */}
          {isAccepted && (
            <span className="badge bg-success">
              Accepted
            </span>
          )}

          <span
            className={`badge bg-${statusColor[operation.status]}`}
          >
            {operation.status}
          </span>
        </div>
      </div>

      {open && (
        <div className="card-body small">
          {/* ========================================================= */}
          {/* SUMMARY */}
          {/* ========================================================= */}
          <div className="row mb-3">
            <div className="col-md-4">
              <strong>Planned Minutes</strong>
              <div>{operation.planned_minutes}</div>
            </div>

            <div className="col-md-4">
              <strong>Suggested From</strong>
              <div>
                {operation.suggested_start_datetime
                  ? new Date(
                      operation.suggested_start_datetime
                    ).toLocaleString()
                  : "—"}
              </div>
            </div>

            <div className="col-md-4">
              <strong>Suggested To</strong>
              <div>
                {operation.suggested_end_datetime
                  ? new Date(
                      operation.suggested_end_datetime
                    ).toLocaleString()
                  : "—"}
              </div>
            </div>
          </div>

          {/* ========================================================= */}
          {/* CONSTRAINT EVALUATION */}
          {/* ========================================================= */}
          {operation.constraints && (
            <div className="mb-3">
              <strong>Constraint Evaluation</strong>

              <table className="table table-sm table-bordered mt-2">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "25%" }}>
                      Constraint
                    </th>
                    <th style={{ width: "15%" }}>
                      Status
                    </th>
                    <th>Explanation</th>
                  </tr>
                </thead>
                <tbody>
                  {operation.constraints.map(
                    (c, idx) => (
                      <tr
                        key={idx}
                        className={
                          c.status === "FAILED"
                            ? "table-danger"
                            : ""
                        }
                      >
                        <td>
                          <strong>{c.constraint}</strong>
                        </td>
                        <td>
                          {c.status === "PASSED" ? (
                            <span className="badge bg-success">
                              ✔ Passed
                            </span>
                          ) : (
                            <span className="badge bg-danger">
                              ✖ Failed
                            </span>
                          )}
                        </td>
                        <td>
                          {c.reason || "—"}
                          {c.available_from && (
                            <div className="text-muted">
                              Available from:{" "}
                              {new Date(
                                c.available_from
                              ).toLocaleString()}
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ========================================================= */}
          {/* LEGACY CHECKPOINTS (BACKWARD COMPATIBLE) */}
          {/* ========================================================= */}
          {operation.checkpoints &&
            operation.checkpoints.length > 0 && (
              <div className="mb-3">
                <strong>Legacy Reasoning</strong>
                <ul className="mb-0 mt-1">
                  {operation.checkpoints.map(
                    (c, idx) => (
                      <li key={idx}>
                        <strong>{c.type}</strong>{" "}
                        —{" "}
                        {c.reason ||
                          "Constraint evaluated"}
                      </li>
                    )
                  )}
                </ul>
              </div>
            )}

          {/* ========================================================= */}
          {/* PRIMARY BLOCKER */}
          {/* ========================================================= */}
          {operation.primary_blocker && (
            <div className="alert alert-danger py-1 mb-3">
              Primary blocker:{" "}
              <strong>
                {operation.primary_blocker}
              </strong>
            </div>
          )}

          {/* ========================================================= */}
          {/* ACTION */}
          {/* ========================================================= */}
          <div className="text-end">
            <button
              className="btn btn-sm btn-outline-success"
              disabled={
                operation.status === "BLOCKED" ||
                isAccepted
              }
              //onClick={onAcceptOperation}
              onClick={() =>onAcceptOperation(operation)}
            >
              ✅ Accept Suggested Schedule
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
